import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { formatDate, formatDuration, formatViews, getCategoryBySlug } from "@/lib/video";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";
import VideoPlayer from "./components/VideoPlayer";

export default function DetailPage() {
  const { videoId = "" } = useParams();
  const video = videos.find((v) => v.id === videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    setToast("");
  }, [videoId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!video) return [];
    return [...videos]
      .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }, [video]);

  if (!video) {
    return (
      <div className="px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="视频不存在或已下线"
          description="你访问的视频可能已被移除，返回首页看看其他热门内容吧。"
          actions={[
            { label: "返回首页", to: "/" },
            { label: "浏览热门视频", to: "/category/funny", variant: "ghost" },
          ]}
        />
      </div>
    );
  }

  const category = getCategoryBySlug(video.categorySlug);
  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享给朋友吧");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const nowFav = toggleFavorite(video.id);
    setToast(nowFav ? "已加入收藏" : "已取消收藏");
  };

  const infoItems = [
    { icon: "ri-calendar-line", label: "发布日期", value: formatDate(video.publishDate) },
    { icon: "ri-price-tag-3-line", label: "所属分类", value: category?.name ?? "综合" },
    { icon: "ri-timer-line", label: "视频时长", value: formatDuration(video.duration) },
    { icon: "ri-eye-line", label: "播放量", value: formatViews(video.views) },
    { icon: "ri-thumb-up-line", label: "点赞数", value: formatViews(video.likes) },
    { icon: "ri-user-line", label: "作者", value: video.author },
  ];

  return (
    <div className="px-4 py-8 md:px-6 md:py-10">
      <div className="mb-4">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: "/" },
            { label: category?.name ?? "综合", to: `/category/${video.categorySlug}` },
            { label: video.title },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <VideoPlayer video={video} />

          <div className="mt-6">
            <h1 className="font-heading text-xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
              {video.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link
                to={`/category/${video.categorySlug}`}
                className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-label text-primary-700 transition-colors duration-200 hover:bg-primary-200"
              >
                <i className={`${category?.icon ?? "ri-price-tag-3-line"} text-base`}></i>
                {category?.name ?? "综合"}
              </Link>
              <span className="text-sm text-foreground-500">
                {formatDate(video.publishDate)} 发布
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-label transition-colors duration-200 cursor-pointer ${
                  favorited
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-lg`}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-label text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-share-line text-lg"></i>
                分享链接
              </button>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">视频简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {video.description}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                本页面为演示内容，视频信息与播放功能均为前端模拟，不提供真实视频流。
              </p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
              {infoItems.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col gap-1 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className={`${item.icon} text-base text-primary-500`}></i>
                    {item.label}
                  </span>
                  <span className="font-label text-sm font-bold text-foreground-900">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside>
          <div className="lg:sticky lg:top-24">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-magic-line text-xl text-primary-500"></i>
              相关推荐
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
            <Link
              to={`/category/${video.categorySlug}`}
              className="mt-4 flex items-center justify-center gap-1 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm font-label text-primary-600 transition-colors duration-200 hover:border-primary-300"
            >
              查看「{category?.name ?? "综合"}」更多视频
              <i className="ri-arrow-right-s-line text-lg"></i>
            </Link>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-3 text-sm font-label text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}